// src/components/artwork/ArtistAutocomplete.jsx
// Autocomplétion d'artiste : propose les artistes du référentiel de côtes (RPC
// public.search_artists, tolérant aux fautes/accents) en affichant nom + dates + rang
// Artprice. À la sélection, remplit le champ (l'utilisateur peut toujours saisir librement).
// Étape 1 (suggestion). onSelectArtist expose l'artiste choisi pour l'étape 2 (lien persistant).
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabaseClient";

export default function ArtistAutocomplete({
  name = "artist",
  id = "artist",
  value = "",
  onChange,
  onSelectArtist,
  className = "",
  placeholder,
}) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const timerRef = useRef(null);
  const suppressRef = useRef(""); // valeur juste sélectionnée → ne pas relancer la recherche
  const focusedRef = useRef(false);

  // Recherche débouncée, uniquement quand l'utilisateur tape dans ce champ.
  useEffect(() => {
    const q = (value || "").trim();
    if (suppressRef.current && q === suppressRef.current) return;
    if (!focusedRef.current) return;
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc("search_artists", { p_q: q, p_limit: 8 });
        if (error) throw error;
        const list = Array.isArray(data) ? data : [];
        setSuggestions(list);
        setOpen(list.length > 0);
        setActive(-1);
      } catch {
        setSuggestions([]);
        setOpen(false);
      }
    }, 250);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [value]);

  // Fermer au clic extérieur.
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = useCallback((cand) => {
    if (!cand) return;
    suppressRef.current = cand.name || "";
    onChange?.({ target: { name, value: cand.name || "" } });
    onSelectArtist?.(cand);
    setOpen(false);
    setSuggestions([]);
    setActive(-1);
  }, [name, onChange, onSelectArtist]);

  const datesOf = (c) => {
    if (!c.birth_year) return "";
    return c.death_year ? `${c.birth_year}–${c.death_year}` : `${c.birth_year}–`;
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { if (active >= 0) { e.preventDefault(); choose(suggestions[active]); } }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        type="text"
        name={name}
        id={id}
        value={value || ""}
        autoComplete="off"
        placeholder={placeholder}
        onChange={(e) => { suppressRef.current = ""; onChange?.(e); }}
        onFocus={() => { focusedRef.current = true; if (suggestions.length) setOpen(true); }}
        onBlur={() => { focusedRef.current = false; }}
        onKeyDown={onKeyDown}
        className={className}
      />
      {open && (
        <ul
          className="absolute z-30 mt-1 w-full bg-white rounded-md shadow-lg max-h-72 overflow-auto"
          style={{ border: "1px solid #e5e0d8" }}
        >
          {suggestions.map((c, i) => (
            <li
              key={c.id || i}
              onMouseDown={(e) => { e.preventDefault(); choose(c); }}
              onMouseEnter={() => setActive(i)}
              className="px-3 py-2 cursor-pointer flex items-center justify-between gap-3"
              style={{ background: i === active ? "#faf6ec" : "#fff" }}
            >
              <span className="min-w-0">
                <span className="block text-sm text-gray-900 truncate" style={{ fontFamily: "Georgia, serif" }}>
                  {c.name}
                </span>
                {(datesOf(c) || c.is_living) && (
                  <span className="block text-xs" style={{ color: "#8a8378" }}>
                    {datesOf(c)}
                    {c.is_living ? (datesOf(c) ? " · " : "") + t("artworkEdit.artist.living", { defaultValue: "vivant·e" }) : ""}
                  </span>
                )}
              </span>
              {c.is_reference && c.artprice_rank && (
                <span
                  className="shrink-0 text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{ background: "#faf6ec", color: "#a9843f", border: "1px solid #e5e0d8" }}
                >
                  {t("artworkEdit.artist.rank", { defaultValue: "Artprice #{{n}}", n: c.artprice_rank })}
                </span>
              )}
            </li>
          ))}
          <li className="px-3 py-1.5 text-[11px]" style={{ color: "#b8b1a4", borderTop: "1px solid #f0ece4" }}>
            {t("artworkEdit.artist.hint", { defaultValue: "Suggestions du référentiel de côtes — saisie libre possible." })}
          </li>
        </ul>
      )}
    </div>
  );
}
